import { GeoNode } from 'domain/subscription/interfaces/Subscription';
import { CheckboxState, CheckboxTreeNodeType } from './CheckboxTree.types';
import { geoNodeSort } from 'utils/geoNodeHelpers';

export const transformDataToNodes = (data: GeoNode[]): CheckboxTreeNodeType[] =>
	data.sort(geoNodeSort).map((item) => ({
		value: item.code,
		label: item.name,
		state: CheckboxState.Unchecked,
		children: item.children?.length
			? transformDataToNodes(item.children)
			: undefined,
	}));

export const setStateRecursively = (
	node: CheckboxTreeNodeType,
	state: CheckboxState,
): CheckboxTreeNodeType => {
	const updatedNode = { ...node, state };
	if (updatedNode.children?.length) {
		updatedNode.children = updatedNode.children.map((child) =>
			setStateRecursively(child, state),
		);
	}
	return updatedNode;
};

export const calculateParentState = (
	children: CheckboxTreeNodeType[],
): CheckboxState => {
	if (!children || children.length === 0) {
		return CheckboxState.Unchecked;
	}

	const firstChildState = children[0].state;
	const allSame = children.every((child) => child.state === firstChildState);

	return allSame ? firstChildState : CheckboxState.Indeterminate;
};

export const computeValuesWithCheckedAncestors = (
	nodes: CheckboxTreeNodeType[],
): Set<string> => {
	const valuesWithCheckedAncestors = new Set<string>();

	const traverse = (
		node: CheckboxTreeNodeType,
		hasCheckedAncestor: boolean,
	) => {
		const nodeIsCheckedOrHasCheckedAncestor =
			hasCheckedAncestor || node.state === CheckboxState.Checked;

		if (node.children) {
			node.children.forEach((child) => {
				if (nodeIsCheckedOrHasCheckedAncestor) {
					valuesWithCheckedAncestors.add(child.value);
				}
				traverse(child, nodeIsCheckedOrHasCheckedAncestor);
			});
		}
	};

	nodes.forEach((node) => traverse(node, false));
	return valuesWithCheckedAncestors;
};

export const toFormState = (nodes: CheckboxTreeNodeType[]): string[] => {
	const selectedValues: string[] = [];
	const valuesWithCheckedAncestors = computeValuesWithCheckedAncestors(nodes);

	const traverse = (node: CheckboxTreeNodeType) => {
		if (node.state === CheckboxState.Checked) {
			if (!valuesWithCheckedAncestors.has(node.value)) {
				selectedValues.push(node.value);
			}
			return;
		}

		if (
			node.state === CheckboxState.Indeterminate &&
			node.children?.length
		) {
			node.children.forEach(traverse);
		}
	};

	nodes.forEach(traverse);
	return Array.from(new Set(selectedValues));
};

export const fromFormState = (
	nodes: CheckboxTreeNodeType[],
	selectedValues: string[],
): CheckboxTreeNodeType[] => {
	const processNode = (node: CheckboxTreeNodeType): CheckboxTreeNodeType => {
		if (selectedValues.includes(node.value)) {
			return setStateRecursively(node, CheckboxState.Checked);
		}

		if (node.children?.length) {
			const updatedChildren = node.children.map(processNode);
			const calculatedState = calculateParentState(updatedChildren);

			return {
				...node,
				state: calculatedState,
				children: updatedChildren,
			};
		}

		return { ...node, state: CheckboxState.Unchecked };
	};

	return nodes.map(processNode);
};
